import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Paper, Typography, Box, CircularProgress, styled } from '@mui/material';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';

import TanstackReactTable from 'core/table/TanstackReactTable';
import useCountries from 'hooks/useCountries';

const Title = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#19273C',
  marginBottom: '16px',
});

const InboundContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const InboundText = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#242323ff',
});

const CountryCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#19273C',
});

const CurrencyCell = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#19273C',
});

const AmountCell = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#19273C',
});

export default function InboundStat() {
  const { isLoading, data, error } = useSelector((state) => state.dashboard.inboundPayment);
  const { data: transactionSummaryData } = useSelector(
    (state) => state.dashboard.transactionSummary,
  );
  const { countries, getCountryNameByIso3Code } = useCountries();

  const [columnVisibility] = useState({});

  const transactionData = transactionSummaryData?.data?.data || null;

  const { tableData, totalTransactions } = useMemo(() => {
    if (!data) {
      return { tableData: [], totalTransactions: 0 };
    }

    let dataArray = [];
    if (Array.isArray(data)) {
      dataArray = data;
    } else if (data.data && Array.isArray(data.data)) {
      dataArray = data.data;
    } else if (data.data && Array.isArray(data.data.data)) {
      dataArray = data.data.data;
    }

    if (dataArray.length === 0) {
      return { tableData: [], totalTransactions: 0 };
    }

    const sortedData = [...dataArray].sort((a, b) => {
      const countryA = (a.sending_country || '').toUpperCase();
      const countryB = (b.sending_country || '').toUpperCase();
      return countryA.localeCompare(countryB);
    });

    const transformedData = [];
    let currentCountry = null;

    sortedData.forEach((item, index) => {
      const countryCode = item.sending_country;
      const currency = item.details?.currrency || '';
      const amount = item.details?.sending_amount || 0;

      if (!countryCode) {
        return;
      }

      const normalizedCountryCode = countryCode.toUpperCase();
      const nextItem = sortedData[index + 1];
      const nextCountryCode = nextItem?.sending_country
        ? nextItem.sending_country.toUpperCase()
        : null;
      const hasNextRowSameCountry = nextCountryCode === normalizedCountryCode;

      if (currentCountry !== normalizedCountryCode) {
        currentCountry = normalizedCountryCode;
        transformedData.push({
          sending_country: countryCode,
          currency: currency,
          pay_in_amount: amount,
          isFirstRow: true,
          hasNextRowSameCountry: hasNextRowSameCountry,
        });
      } else {
        transformedData.push({
          sending_country: countryCode,
          currency: currency,
          pay_in_amount: amount,
          isFirstRow: false,
          hasNextRowSameCountry: hasNextRowSameCountry,
        });
      }
    });

    return {
      tableData: transformedData,
      totalTransactions: dataArray.length,
    };
  }, [data]);

  const columns = useMemo(() => {
    const getIso2FromIso3 = (iso3) => {
      if (!iso3 || !countries.length) return '';
      const country = countries.find((c) => c.iso3?.toLowerCase() === iso3?.toLowerCase());
      return country?.iso2?.toLowerCase() || '';
    };

    return [
      {
        header: 'Sending Country',
        accessorFn: (row) => row.sending_country,
        cell: (info) => {
          const row = info.row.original;
          if (!row.isFirstRow) {
            return null;
          }

          const countryCode = row.sending_country;
          const countryName = getCountryNameByIso3Code(countryCode);
          const iso2 = getIso2FromIso3(countryCode);

          return (
            <CountryCell>
              {iso2 && (
                <img
                  loading="lazy"
                  width="20"
                  height="15"
                  src={`https://flagcdn.com/w20/${iso2}.png`}
                  srcSet={`https://flagcdn.com/w40/${iso2}.png 2x`}
                  alt=""
                  style={{ borderRadius: '2px' }}
                />
              )}
              <Typography
                component="span"
                sx={{ fontSize: '14px', fontWeight: 500, color: '#19273C' }}
              >
                {countryName}
              </Typography>
            </CountryCell>
          );
        },
      },
      {
        header: 'Pay In Currency',
        meta: {
          align: 'right',
        },
        accessorFn: (row) => row.currency,
        cell: (info) => <CurrencyCell>{info.getValue() || '-'}</CurrencyCell>,
      },
      {
        header: 'Total Pay In Amount',
        accessorFn: (row) => row.pay_in_amount,
        meta: {
          align: 'right',
        },
        cell: (info) => {
          const amount = info.getValue();
          const currency = info.row.original.currency;

          return (
            <AmountCell>
              {currency
                ? `${currency} ${amount?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : amount?.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
            </AmountCell>
          );
        },
      },
    ];
  }, [getCountryNameByIso3Code, countries]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: { columnVisibility },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Paper sx={{ p: '20px', borderRadius: '16px', height: '100%' }}>
      <Title>Inbound Payment</Title>

      {isLoading ? (
        <Box display="flex" justifyContent="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" minHeight="200px">
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <>
          <InboundContainer>
            <InboundText>
              Total Number of Inbound Transactions:{' '}
              {transactionData?.totalTransactionsCount?.toLocaleString() || '0'}
            </InboundText>
          </InboundContainer>
          {/* {tableData.length === 0 ? (
            <Box sx={{pt: 2, textAlign: 'left' }}>
              <Typography color="text.secondary">No Data available</Typography>
            </Box>
          ) : ( */}
          <TanstackReactTable
            table={table}
            isLoading={isLoading}
            data={tableData}
            columnsCount={columns.length}
            noHeaderBackground
          />
          {/* )} */}
        </>
      )}
    </Paper>
  );
}
